import { useState } from 'react';

import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';

import { IconBriefcase, IconHome, IconTrash } from '@tabler/icons-react';

import { useRouter } from '@tanstack/react-router';

import { Button, Group, SimpleGrid, Stack, Text, TextInput } from '@optiro/ui';

import { Alert } from '@/components/Alert/Alert';
import { SelectCategory } from '@/components/Forms/ProjectForm/SelectCategory/SelectCategory';
import SelectManager from '@/components/Forms/ProjectForm/SelectManager/SelectManager';
import useDeleteProject from '@/hooks/projects/useDeleteProject';
import { Project } from '@/types/types';
import { renderDay } from '@/utils/renderDay';

type ProjectFormProps = {
  mode: 'create' | 'update';
  onClose: () => void;
  defaultValues?: Partial<Project>;
  onSubmit: (values: FormValues) => void;
};

export type FormValues = {
  name: string;
  manager_ids: (string | number)[];
  category_id: number | undefined;
  client_name: string;
  expected_follow_up_date?: string;
  start_date?: string;
  end_date?: string;
};

export const ProjectForm = ({ mode, onClose, onSubmit, defaultValues = {} }: ProjectFormProps) => {
  const [openAlert, setOpenAlert] = useState(false);
  const deleteMutation = useDeleteProject();
  const { navigate } = useRouter();

  const form = useForm({
    initialValues: {
      name: defaultValues.name || '',
      description: defaultValues.description || '',
      client_name: defaultValues.client_name || '',
      manager_ids: defaultValues.manager_ids || [],
      category_id: defaultValues.category_id || undefined,
      expected_follow_up_date: defaultValues.expected_follow_up_date || undefined,
      start_date: defaultValues.start_date || undefined,
      end_date: defaultValues.end_date || undefined,
    },
    validate: {
      name: (value) => (value ? null : 'Navn er påkrævet'),
    },
  });

  const onDelete = () => {
    setOpenAlert(true);
  };

  const handleDelete = () => {
    if (!defaultValues.id) return;

    deleteMutation.mutate(defaultValues.id, {
      onSuccess: () => {
        setOpenAlert(false);
        onClose();
        navigate({ to: '/app/projects', replace: true });
      },
    });
  };

  const isEdit = mode === 'update';

  return (
    <form id="form" onSubmit={form.onSubmit((values) => onSubmit(values))}>
      <Alert
        open={openAlert}
        setClose={setOpenAlert}
        title={'Slet projekt'}
        onSubmit={handleDelete}
      >
        <Text>
          Er du sikker på, at du vil slette dette projekt? Denne handling kan ikke fortrydes.
          Projekter der vil blive slettet permanent, og alle tilknyttede data vil gå tabt.
        </Text>
      </Alert>

      <Stack>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <TextInput
            label="Navn"
            key={form.key('name')}
            placeholder="Udvikling for Acme ApS"
            size="md"
            required
            leftSection={<IconHome size={16} />}
            {...form.getInputProps('name')}
          />
          <SelectManager
            value={form.values.manager_ids}
            label="Ansvarlig"
            onChange={(value) => form.setFieldValue('manager_ids', value)}
          />
        </SimpleGrid>

        <SelectCategory
          size="md"
          onChange={(value) => {
            form.setFieldValue('category_id', Number(value));
          }}
          value={String(form.values.category_id)}
        />

        <TextInput
          label="Kunde"
          key={form.key('client_name')}
          placeholder="F.eks.. Acme ApS"
          size="md"
          leftSection={<IconBriefcase size={16} />}
          {...form.getInputProps('client_name')}
        />

        {defaultValues.status == 'pending' && (
          <DatePickerInput
            label="Forventet opfølgningsdato"
            size="md"
            withWeekNumbers
            renderDay={renderDay}
            {...form.getInputProps('expected_follow_up_date')}
          />
        )}

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <DatePickerInput
            label="Startdato"
            placeholder="Vælg dato"
            size="md"
            clearable
            valueFormat="DD/MM/YYYY"
            withWeekNumbers
            renderDay={renderDay}
            {...form.getInputProps('start_date')}
          />
          <DatePickerInput
            label="Slutdato"
            placeholder="Vælg dato"
            size="md"
            clearable
            valueFormat="DD/MM/YYYY"
            withWeekNumbers
            renderDay={renderDay}
            {...form.getInputProps('end_date')}
          />
        </SimpleGrid>

        <Group justify="space-between" mt="md">
          {isEdit && (
            <Button
              variant="outline"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={onDelete}
            >
              Slet projekt
            </Button>
          )}

          <Button type="submit">{isEdit ? 'Opdater' : 'Opret'} projekt</Button>
        </Group>
      </Stack>
    </form>
  );
};
